import styled from '@emotion/styled';
import { ChangeEvent, MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import Switch from '@mui/material/Switch';
import { buyerSeatService } from '../../api/services/buyer-seat.service';
import { applicationService } from '../../api/services/application.service';
import { BuyerSeatDetailDto, BuyerSeatUpdateDto } from '../../api/services/dto/buyer-seat-dto';
import { DealOverviewDto } from '../../api/services/dto/deal-overview-dto';
import { OrganizationDto } from '../../api/services/dto/user-managment-dto/organization-dto';
import { DEAL_STATUS } from '../../util/deals-util';
import { useAlert } from '../shared/AlertProvider';
import { warrantService } from '../../api/services/warrant.service';
import BuyerSeatBreadcrumb from './BuyerSeatBreadcrumb';
import { getColor } from '../../theme/colors/colors';

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }: TablePaginationActionsProps) => (
  <Box sx={{ flexShrink: 0, ml: 2.5 }}>
    <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
      <KeyboardArrowLeft />
    </IconButton>
    <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
      <KeyboardArrowRight />
    </IconButton>
  </Box>
);

const SSP_OPTIONS = ['Xandr', 'Magnite', 'PubMatic', 'Google Ad Manager'];

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
};

const BuyerSeatDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { setAlert } = useAlert();

  const [seat, setSeat] = useState<BuyerSeatDetailDto | null>(null);
  const [editValues, setEditValues] = useState<BuyerSeatUpdateDto>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Organization autocomplete state
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);

  // Deals table state
  const [deals, setDeals] = useState<DealOverviewDto[]>([]);
  const [dealsTotal, setDealsTotal] = useState(0);
  const [dPage, setDPage] = useState(0);
  const [dRowsPerPage, setDRowsPerPage] = useState(10);
  const [dSearch, setDSearch] = useState('');
  const [dStatus, setDStatus] = useState<string>('ALL');

  // Load buyer seat details
  useEffect(() => {
    if (!id) return;
    buyerSeatService
      .getBuyerSeatById(Number(id))
      .then(setSeat)
      .catch((reason) =>
        setAlert({ open: true, severity: 'error', title: 'Failed to load buyer seat', message: reason.message })
      );
  }, [id]);

  // Load organizations for autocomplete
  useEffect(() => {
    warrantService.fetchOrganizationsOrderedAlphabetically().then(setOrganizations).catch(() => {});
  }, []);

  // Click-outside handler to exit edit mode
  useEffect(() => {
    if (!isEditing) return;
    const handleMouseDown = (e: Event) => {
      const target = e.target as Element;
      const isInCard = cardRef.current?.contains(target);
      const isInActions = actionsRef.current?.contains(target);
      const isInPortal =
        !!target.closest('[role="listbox"]') ||
        !!target.closest('[role="presentation"]') ||
        !!target.closest('.MuiPopover-root') ||
        !!target.closest('.MuiModal-root') ||
        !!target.closest('.MuiAutocomplete-popper');
      if (!isInCard && !isInActions && !isInPortal) {
        setIsEditing(false);
        setEditValues({});
        setIsDirty(false);
        setSelectedOrg(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isEditing]);

  // Load deals
  const fetchDeals = useCallback(
    (page: number, size: number, search: string, status: string | null) => {
      if (!id) return;
      if (status === 'ALL') status = '';
      applicationService
        .getDealsByBuyerSeat(Number(id), {
          page,
          size,
          search: search || undefined,
          status: status || undefined,
          sort: 'auditMetadata.createdDate,desc',
        })
        .then((res) => {
          setDeals(res.resultList);
          setDealsTotal(Number(res.totalItems));
        })
        .catch(() => {});
    },
    [id]
  );

  useEffect(() => {
    fetchDeals(dPage, dRowsPerPage, dSearch, dStatus);
  }, [fetchDeals, dPage, dRowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => fetchDeals(0, dRowsPerPage, dSearch, dStatus), 400);
    return () => clearTimeout(timer);
  }, [dSearch, dStatus]);

  // Edit mode helpers
  const enterEditMode = () => {
    if (!seat || isEditing) return;
    setEditValues({
      name: seat.name,
      ssp: seat.ssp,
      disabled: seat.disabled,
    });
    const matchingOrg = organizations.find((o) => o.organizationId === seat.tenantId);
    setSelectedOrg(matchingOrg || null);
    setIsEditing(true);
  };

  const handleFieldChange = (field: keyof BuyerSeatUpdateDto, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues({});
    setIsDirty(false);
    setSelectedOrg(null);
  };

  const handleSave = async () => {
    if (!id || !seat) return;
    try {
      const updatePayload: BuyerSeatUpdateDto = {
        ...editValues,
        ...(selectedOrg ? { tenantId: selectedOrg.organizationId } : {}),
      };
      const updated = await buyerSeatService.updateBuyerSeat(Number(id), updatePayload);
      setSeat(updated);
      setIsEditing(false);
      setEditValues({});
      setIsDirty(false);
      setAlert({ open: true, severity: 'success', title: 'Saved', message: 'Buyer seat updated.' });
    } catch (reason: any) {
      setAlert({ open: true, severity: 'error', title: 'Save failed', message: reason.message });
    }
  };

  if (!seat) return null;

  return (
    <PageWrapper>
      <BuyerSeatBreadcrumb
        items={[
          { label: 'Buyer Seats', to: '/buyer-seat/list' },
          { label: seat.name },
        ]}
      />

      <PageTitleRow>
        <TitleLeft>
          <SeatName>{seat.name}</SeatName>
          <SeatIdLabel>Seat ID: {seat.buyerSeatId}</SeatIdLabel>
        </TitleLeft>
        <TitleActions ref={actionsRef}>
          <Button variant="outlined" size="medium" onClick={handleCancel}>
            Cancel
          </Button>
          {isDirty && (
            <Button variant="contained" size="medium" onClick={handleSave}>
              Save
            </Button>
          )}
        </TitleActions>
      </PageTitleRow>

      {/* Card 1: Basic Info */}
      <Card
        ref={cardRef}
        onClick={enterEditMode}
        style={{ cursor: isEditing ? 'default' : 'pointer' }}
      >
        <CardTitle>Basic Information</CardTitle>
        <FieldsGrid>
          {/* Row 1 */}
          <FieldBlock>
            <FieldLabel>
              Buyer Seat Name
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <TextField
                size="small"
                value={editValues.name ?? ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                fullWidth
              />
            ) : (
              <FieldValue>{seat.name}</FieldValue>
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Buyer Seat ID</FieldLabel>
            <FieldValue>{seat.buyerSeatId}</FieldValue>
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>
              Organization
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <div onClick={(e) => e.stopPropagation()}>
                <Autocomplete
                  size="small"
                  options={organizations}
                  getOptionLabel={(opt) => opt.name || ''}
                  value={selectedOrg}
                  onChange={(_: SyntheticEvent, newValue: OrganizationDto | null) => {
                    setSelectedOrg(newValue);
                    setIsDirty(true);
                  }}
                  isOptionEqualToValue={(opt, val) => opt.organizationId === val.organizationId}
                  renderInput={(params) => <TextField {...params} placeholder="Select organization" />}
                  fullWidth
                />
              </div>
            ) : (
              <FieldValue>{seat.organizationName || '—'}</FieldValue>
            )}
          </FieldBlock>

          {/* Row 2 */}
          <FieldBlock>
            <FieldLabel>
              SSP
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <TextField
                select
                size="small"
                variant="outlined"
                value={editValues.ssp ?? seat.ssp}
                onChange={(e) => handleFieldChange('ssp', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
              >
                {SSP_OPTIONS.map((ssp) => (
                  <MenuItem key={ssp} value={ssp}>{ssp}</MenuItem>
                ))}
              </TextField>
            ) : (
              <FieldValue>{seat.ssp || '—'}</FieldValue>
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>
              Status
              {!isEditing && <EditIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.4 }} />}
            </FieldLabel>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={!(editValues.disabled ?? seat.disabled)}
                  onChange={(e) => {
                    setEditValues((prev) => ({ ...prev, disabled: !e.target.checked }));
                    setIsDirty(true);
                  }}
                  size="small"
                />
                <span style={{ fontSize: 14 }}>
                  {(editValues.disabled ?? seat.disabled) ? 'Disabled' : 'Enabled'}
                </span>
              </Box>
            ) : (
              <Chip
                label={
                  seat.status === 'ACTIVE'
                    ? 'Active'
                    : seat.status === 'DISABLED'
                    ? 'Disabled'
                    : 'Inactive'
                }
                className={`table-chip ${
                  seat.status === 'ACTIVE'
                    ? 'organization-status-active'
                    : 'organization-status-inactive'
                }`}
                size="small"
              />
            )}
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Created Date</FieldLabel>
            <FieldValue>{formatDate(seat.createdAt)}</FieldValue>
          </FieldBlock>

          {/* Row 3 */}
          <FieldBlock>
            <FieldLabel>Total Deals</FieldLabel>
            <FieldValue>{seat.dealsCount}</FieldValue>
          </FieldBlock>
        </FieldsGrid>
      </Card>

      {/* Card 2: Associated Deals */}
      <Card>
        <CardHeaderRow>
          <CardTitle>Associated Deals ({dealsTotal})</CardTitle>
          <CardControls>
            <TextField
              select
              size="small"
              variant="outlined"
              value={dStatus}
              onChange={(e) => { setDStatus(e.target.value); setDPage(0); }}
              SelectProps={{
                renderValue: (val) => {
                  const labels: Record<string, string> = {
                    ALL: 'All',
                    [DEAL_STATUS.ACTIVE]: 'Active',
                    [DEAL_STATUS.IN_CREATION]: 'In Creation',
                    [DEAL_STATUS.DRAFT]: 'Draft',
                    [DEAL_STATUS.ERROR]: 'Error',
                  };
                  return labels[val as string] ?? String(val);
                },
              }}
              sx={{
                minWidth: 140,
                '& .MuiOutlinedInput-root': { backgroundColor: '#F5F5F5' },
              }}
            >
              {[
                { value: 'ALL', label: 'All' },
                { value: DEAL_STATUS.ACTIVE, label: 'Active' },
                { value: DEAL_STATUS.IN_CREATION, label: 'In Creation' },
                { value: DEAL_STATUS.DRAFT, label: 'Draft' },
                { value: DEAL_STATUS.ERROR, label: 'Error' },
              ].map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    backgroundColor: dStatus === opt.value ? '#F5F5F5' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box component="span" sx={{ flexGrow: 1 }}>{opt.label}</Box>
                  {dStatus === opt.value && (
                    <CheckIcon sx={{ color: 'primary.main', ml: 2 }} fontSize="small" />
                  )}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              placeholder="Search by Name or Deal ID"
              size="small"
              value={dSearch}
              onChange={(e) => { setDSearch(e.target.value); setDPage(0); }}
              sx={{ minWidth: 330 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {dSearch ? (
                      <IconButton size="small" onClick={() => setDSearch('')} sx={{ p: 0.5 }}>
                        <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    ) : (
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </CardControls>
        </CardHeaderRow>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#F5F5F5', borderTop: '1px solid #0000001A' }}>
                <TableCell>Deal ID</TableCell>
                <TableCell>Deal Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.length ? (
                deals.map((deal) => (
                  <TableRow key={deal.id}>
                    <TableCell>{deal.xandrDealId}</TableCell>
                    <TableCell>{deal.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          deal.status === DEAL_STATUS.ACTIVE
                            ? 'Active'
                            : deal.status === DEAL_STATUS.DRAFT
                            ? 'Draft'
                            : deal.status === DEAL_STATUS.IN_CREATION
                            ? 'In creation'
                            : deal.status === DEAL_STATUS.ERROR
                            ? 'Error'
                            : deal.status
                        }
                        className={`table-chip ${
                          deal.status === DEAL_STATUS.ACTIVE
                            ? 'deal-status-active'
                            : deal.status === DEAL_STATUS.DRAFT
                            ? 'deal-status-draft'
                            : deal.status === DEAL_STATUS.IN_CREATION
                            ? 'deal-status-in-creation'
                            : deal.status === DEAL_STATUS.ERROR
                            ? 'deal-status-error'
                            : ''
                        }`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(deal.createdDate)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoMatches>No deals found</NoMatches>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {deals.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    colSpan={4}
                    count={dealsTotal}
                    rowsPerPage={dRowsPerPage}
                    page={dPage}
                    SelectProps={{ native: true }}
                    onPageChange={(_e: MouseEvent<HTMLButtonElement> | null, newPage: number) => setDPage(newPage)}
                    onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setDRowsPerPage(parseInt(e.target.value, 10));
                      setDPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Card>
    </PageWrapper>
  );
};

export default BuyerSeatDetails;

// ─── Styled components ───────────────────────────────────────────────────────

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PageTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TitleLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SeatName = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  color: ${getColor('text')};
`;

const SeatIdLabel = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
`;

const TitleActions = styled.div`
  display: flex;
  gap: 8px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #0000001a;
  border-radius: 8px;
  padding: 20px 24px;
`;

const CardTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 500;
  color: ${getColor('text')};
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 32px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 2px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const FieldValue = styled.span`
  font-size: 14px;
  color: ${getColor('text')};
`;

const NoMatches = styled.div`
  font-size: 14px;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px;
  color: rgba(0, 0, 0, 0.4);
`;
